import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin.");
      navigate("/admin");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-navy text-white flex items-center justify-center px-6"
    >
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }} />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-gold text-sm mb-8">
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl p-10">
          <div className="overline text-gold mb-3">Admin · D32 India</div>
          <h1 className="text-3xl font-display font-semibold">Sign in</h1>
          <p className="mt-2 text-sm text-gray-400">
            Manage members, circles, and submissions.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="admin-input-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 bg-white/[0.05] border-white/10 text-white placeholder:text-gray-500"
                placeholder="admin@d32businessnetwork.in"
              />
            </div>
            <div>
              <Label htmlFor="pw" className="text-gray-300">Password</Label>
              <Input
                id="pw"
                type="password"
                required
                data-testid="admin-input-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 bg-white/[0.05] border-white/10 text-white"
              />
            </div>
            {error && (
              <div data-testid="admin-login-error" className="text-sm text-red-400">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-white font-semibold py-3 rounded-full hover:bg-[#5fc1f1] transition disabled:opacity-60"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              Sign In
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
